import React, { useEffect, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TeamManagementService } from "@application/useCases/workspace/TeamManagementService";
import { UserDirectoryService } from "@application/useCases/workspace/UserDirectoryService";
import { UserPicker } from "@presentation/components/team/UserPicker";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { describeManagerInvariantDenial } from "@presentation/common/utils/managerInvariantDenial";
import { describeTeamRolePermissions } from "@presentation/common/utils/roleDescriptions";
import { TEAM_ROLE_NAMES, TeamRoles } from "@domain/schemas/workspace";
import type { TeamDetail, TeamRoleName, UserSummary } from "@domain/schemas/workspace";
import { useWorkspaceSuccessNotification } from "@presentation/components/notifications/useWorkspaceSuccessNotification";

interface AssignTeamRoleDialogProps {
  teamId: string;
  /**
  Present for "change role" (user field locked); absent for "add member" (empty, editable user field).
  */
  existingMember?: { userId: string; role: TeamRoleName };
  /**
  Current team members' user ids, so "add member" search results exclude people already on the team.
  */
  existingMemberUserIds?: readonly string[];
  onClose: () => void;
  onSuccess: (updated: TeamDetail) => void;
}

/**
 * One dialog for both add-member and change-role: there is no "member with no role", so the two
 * are the same backend call, distinguished only by whether the user field is pre-filled and locked.
 *
 * The user field is a searchable picker backed by `GET /users?search=` (SF-2783); for "change role"
 * it is locked and its display text is resolved via `GET /users?ids=` rather than showing the raw id.
 */
export const AssignTeamRoleDialog: React.FC<AssignTeamRoleDialogProps> = ({ teamId, existingMember, existingMemberUserIds, onClose, onSuccess }) => {
  const teamManagementService = useInjection(TeamManagementService);
  const notifySuccess = useWorkspaceSuccessNotification();
  const userDirectoryService = useInjection(UserDirectoryService);
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(existingMember ? { id: existingMember.userId, username: existingMember.userId, displayName: existingMember.userId } : null);
  const [role, setRole] = useState<TeamRoleName>(existingMember?.role ?? TeamRoles.Member);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isChangeRole = Boolean(existingMember);
  const canSubmit = Boolean(selectedUser) && !isSubmitting;

  useEffect(() => {
    if (!existingMember) return;
    let isCancelled = false;
    void userDirectoryService.getUsersByIdsAsync([existingMember.userId]).then((users) => {
      if (isCancelled || users.length === 0) return;
      setSelectedUser(users[0]);
    });
    return () => {
      isCancelled = true;
    };
  }, [existingMember?.userId]);

  const handleSubmit = async () => {
    if (!selectedUser) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const updated = await teamManagementService.assignTeamRoleAsync(teamId, selectedUser.id, role);
      notifySuccess("team", isChangeRole ? "roleChanged" : "memberAdded");
      onSuccess(updated);
      onClose();
    } catch (error) {
      setErrorMessage(describeManagerInvariantDenial(error, "team") ?? extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not save the role assignment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog title={isChangeRole ? "Change member role" : "Add member"} onClose={onClose} width={420}>
      <div style={{ display: "grid", gap: 16 }}>
        <div>
          <label htmlFor="assign-team-role-user-id" style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            User
          </label>
          <UserPicker id="assign-team-role-user-id" value={selectedUser} onChange={setSelectedUser} disabled={isChangeRole} excludeUserIds={existingMemberUserIds} />
        </div>
        <div>
          <label htmlFor="assign-team-role-role" style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            Role
          </label>
          <DropDownList
            id="assign-team-role-role"
            style={{ width: "100%" }}
            data={[...TEAM_ROLE_NAMES]}
            value={role}
            onChange={(event) => setRole(event.value as TeamRoleName)}
            valueRender={(element, value) => (value ? React.cloneElement(element, { title: describeTeamRolePermissions(value) }) : element)}
            itemRender={(li, itemProperties) => React.cloneElement(li, { title: describeTeamRolePermissions(itemProperties.dataItem as TeamRoleName) })}
          />
        </div>
        {errorMessage && <div style={{ color: "#7f1d1d" }}>{errorMessage}</div>}
      </div>
      <DialogActionsBar>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button themeColor="primary" disabled={!canSubmit} onClick={handleSubmit}>
          {isChangeRole ? "Save role" : "Add member"}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
